// PlayerAvatar — deterministic generative SVG avatar (initials over a
// two-color gradient derived from the id). Accepts an optional `src`
// to override with a real portrait asset in the future.

import { useState } from 'react'

// 8 brand-anchored color pairs [from, to]
const PALETTES: Array<[string, string]> = [
  ['#0E7490', '#38BDF8'],
  ['#15803D', '#4ADE80'],
  ['#9A3412', '#FB923C'],
  ['#1E40AF', '#60A5FA'],
  ['#6D28D9', '#A78BFA'],
  ['#0F766E', '#2DD4BF'],
  ['#A16207', '#FBBF24'],
  ['#BE123C', '#FB7185'],
]

function hashString(value: string): number {
  let hash = 0
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0
  }
  return hash
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export interface PlayerAvatarProps {
  id: string // determines colors and accent shape
  name: string
  size?: number
  src?: string // optional portrait override (falls back to SVG on error)
}

export function PlayerAvatar({ id, name, size = 36, src }: PlayerAvatarProps) {
  const [imageOk, setImageOk] = useState(Boolean(src))
  const hash = hashString(id)
  const [from, to] = PALETTES[hash % PALETTES.length]
  const accentVariant = Math.floor(hash / 8) % 3
  const initials = getInitials(name)

  if (src && imageOk) {
    return (
      <span className="avatar" style={{ width: size, height: size }}>
        <img
          src={src}
          alt={name}
          width={size}
          height={size}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={() => setImageOk(false)}
        />
      </span>
    )
  }

  return (
    <span className="avatar" style={{ width: size, height: size }} title={name}>
      <svg viewBox="0 0 40 40" width={size} height={size} role="img" aria-label={name}>
        <defs>
          <linearGradient id={`av-${hash}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={from} />
            <stop offset="100%" stopColor={to} />
          </linearGradient>
        </defs>
        <rect width="40" height="40" fill={`url(#av-${hash})`} />
        {/* Accent shape variation */}
        {accentVariant === 0 && <circle cx="33" cy="7" r="10" fill="rgba(255,255,255,0.12)" />}
        {accentVariant === 1 && <rect x="-6" y="26" width="30" height="30" rx="6" transform="rotate(-12 8 40)" fill="rgba(255,255,255,0.10)" />}
        {accentVariant === 2 && <polygon points="40,18 40,40 14,40" fill="rgba(0,0,0,0.14)" />}
        <text
          x="20"
          y="25.5"
          textAnchor="middle"
          fontFamily="'Exo 2', system-ui, sans-serif"
          fontWeight="700"
          fontSize="15"
          fill="#fff"
        >
          {initials}
        </text>
      </svg>
    </span>
  )
}
